import { SortOption, type DirectoryFilters } from '@/components/client/directory/use-directory-reducer'

import type { DirectoryRelease } from './aggregate-releases'
import { presentReleaseForFilters } from './filter-release-products'

// Pure list shaping for the directory: brand + product-level facets, then a
// name/brand text match (3+ characters, like the old directory), then the sort.

export const filterReleases = (
  releases: DirectoryRelease[],
  filters: DirectoryFilters,
  term = ''
): DirectoryRelease[] => {
  const filtered: DirectoryRelease[] = []
  const lowerTerm = term.trim().toLowerCase()
  const useTerm = lowerTerm.length >= 3

  for (const release of releases) {
    if (filters.brand && release.brand !== filters.brand) {
      continue
    }

    const presentedRelease = presentReleaseForFilters(release, filters)
    if (!presentedRelease) {
      continue
    }

    if (
      useTerm &&
      !presentedRelease.fullName.toLowerCase().includes(lowerTerm) &&
      !presentedRelease.brandName.toLowerCase().includes(lowerTerm) &&
      !presentedRelease.brand.toLowerCase().includes(lowerTerm)
    ) {
      continue
    }

    filtered.push(presentedRelease)
  }

  return filtered
}

// "Featured" is the server order (starred `models.featured` picks, then real
// HT score); the other sorts are stable re-orders of that list, unrated
// families last.
export const sortReleases = (releases: DirectoryRelease[], sort: SortOption): DirectoryRelease[] => {
  if (sort === SortOption.Featured) {
    return releases
  }

  const sorted = [...releases]
  switch (sort) {
    case SortOption.TopRated:
      sorted.sort((a, b) => (b.score ?? -1) - (a.score ?? -1))
      break
    case SortOption.Newest:
      sorted.sort((a, b) => (b.releaseDate ?? '').localeCompare(a.releaseDate ?? ''))
      break
    case SortOption.PriceLow:
      sorted.sort((a, b) => (a.fromPrice ?? Infinity) - (b.fromPrice ?? Infinity))
      break
    case SortOption.PriceHigh:
      sorted.sort((a, b) => (b.fromPrice ?? 0) - (a.fromPrice ?? 0))
      break
  }
  return sorted
}

export const DIRECTORY_PAGE_SIZE = 10

export interface DirectoryPage {
  items: DirectoryRelease[]
  /** The page actually shown — clamped into 1..pageCount. */
  page: number
  pageCount: number
  /** 1-based positions of the first and last row on the page (0–0 when empty). */
  start: number
  end: number
  total: number
}

// Ten families per page, so the page renders ten rows instead of every
// family. The data set stays in memory for the pure filter/sort above; only
// the rendered slice is paged. An out-of-range page clamps rather than
// rendering nothing.
export const paginateReleases = (releases: DirectoryRelease[], requestedPage: number): DirectoryPage => {
  const total = releases.length
  const pageCount = Math.max(1, Math.ceil(total / DIRECTORY_PAGE_SIZE))
  const page = Math.min(Math.max(1, Math.floor(requestedPage) || 1), pageCount)
  const offset = (page - 1) * DIRECTORY_PAGE_SIZE
  const items = releases.slice(offset, offset + DIRECTORY_PAGE_SIZE)
  return {
    items,
    page,
    pageCount,
    start: total === 0 ? 0 : offset + 1,
    end: offset + items.length,
    total,
  }
}
